import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import { routes } from '@/router/routes'
import type { RouteWithMeta } from '@/router/types'
import {ChevronDownIcon, ChevronUpIcon} from "@radix-ui/react-icons"

interface MenuItem {
  label: string
  path: string
  icon?: string
  children?: MenuItem[]
}

// 라우트에서 메뉴 아이템 생성
const buildMenuItems = (routeList: RouteWithMeta[], parentPath = ''): MenuItem[] => {
  const items: MenuItem[] = []

  routeList.forEach(route => {
    // showInMenu가 true인 경우에만 메뉴에 추가
    if (route.meta?.showInMenu) {
      // path가 /로 시작하면 절대 경로, 아니면 부모 경로에 연결
      const fullPath = route.path?.startsWith('/')
        ? route.path
        : `${parentPath}/${route.path || ''}`.replace(/\/+/g, '/').replace(/\/$/, '') || '/'

      const menuItem: MenuItem = {
        label: route.meta.label || '',
        path: fullPath,
        icon: route.meta.icon
      }

      // children이 있고 그 중에 showInMenu가 true인 것이 있으면 children 추가
      if (route.children && route.children.length > 0) {
        const childMenuItems = buildMenuItems(route.children, fullPath)
        if (childMenuItems.length > 0) {
          menuItem.children = childMenuItems
        }
      }

      items.push(menuItem)
    }
  })

  return items
}

// routes에서 AdminLayout의 children만 추출
const getAdminRoutes = (): RouteWithMeta[] => {
  // '/' 경로 찾기
  const rootRoute = routes.find(r => r.path === '/')
  if (!rootRoute?.children) return []

  // AdminLayout의 children 찾기
  const adminLayoutRoute = rootRoute.children.find(r => r.path === '')
  if (!adminLayoutRoute?.children) return []

  return adminLayoutRoute.children
}

export default function Sidebar() {
  const location = useLocation()
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])

  // 라우터에서 메뉴 생성 (메모이제이션하여 무한 루프 방지)
  const adminRoutes = useMemo(() => getAdminRoutes(), [])
  const menuStructure = useMemo(() => buildMenuItems(adminRoutes), [adminRoutes])

  // 현재 경로에 맞는 부모 메뉴를 자동으로 펼침
  useEffect(() => {
    const currentPath = location.pathname
    const parentsToExpand: string[] = []

    // 메뉴 구조를 순회하며 현재 경로의 부모 찾기
    menuStructure.forEach(item => {
      if (item.children) {
        // 하위 메뉴 중에 현재 경로와 일치하는 것이 있는지 확인
        const hasActiveChild = item.children.some(child =>
          currentPath === child.path || currentPath.startsWith(child.path + '/')
        )

        if (hasActiveChild && !parentsToExpand.includes(item.path)) {
          parentsToExpand.push(item.path)
        }
      }
    })

    // 펼쳐야 할 메뉴가 있으면 expandedMenus에 추가
    if (parentsToExpand.length > 0) {
      setExpandedMenus(prev => {
        // 이미 모든 필요한 메뉴가 펼쳐져 있는지 확인
        const allExpanded = parentsToExpand.every(path => prev.includes(path))
        if (allExpanded) {
          return prev
        }

        // 새로운 메뉴만 추가
        const newExpanded = [...prev]
        parentsToExpand.forEach(path => {
          if (!newExpanded.includes(path)) {
            newExpanded.push(path)
          }
        })
        return newExpanded
      })
    }
  }, [location.pathname, menuStructure])

  const toggleMenu = (path: string) => {
    setExpandedMenus(prev =>
      prev.includes(path)
        ? prev.filter(p => p !== path)
        : [...prev, path]
    )
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <aside className="w-64 bg-[var(--Stone-100)] text-[var(--Stone-900)] min-h-screen flex flex-col">
      <nav className="">
        {menuStructure.map(item => (
          <div key={item.path}>
            {item.children ? (
              <>
                <button
                  onClick={() => toggleMenu(item.path)}
                  className={`w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--dub-gray-4)] transition-colors ${
                    isActive(item.path) ? 'text-[var(--Pudding-500)]' : ''
                  }`}
                >
                  <div className="flex items-center" >
                    {item.icon && <span className="mr-3 text-lg" style={{ fontSize: '16px', fontWeight: '500' }}>{item.icon}</span>}
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{item.label}</span>
                  </div>
                  <span>
                    {expandedMenus.includes(item.path) ? <ChevronUpIcon/> : <ChevronDownIcon/>}
                  </span>
                </button>

                {expandedMenus.includes(item.path) && (
                  <div className="bg-[var(--dub-gray-f)]">
                    {item.children.map(child => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`block px-4 py-2 pl-12 text-sm hover:bg-[var(--dub-gray-4)] transition-colors ${
                          location.pathname === child.path || location.pathname.startsWith(child.path + '/')
                            ? ' font-medium text-[var(--Pudding-500)]'
                            : ''
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link
                to={item.path}
                className={`flex items-center px-4 py-3 hover:bg-[var(--dub-gray-4)] transition-colors ${
                  isActive(item.path) ? 'text-[var(--Pudding-500)]' : ''
                }`}
              >
                {item.icon && <span className="mr-3 text-lg" style={{ fontSize: '16px', fontWeight: '500' }}>{item.icon}</span>}
                <span style={{ fontSize: '16px', fontWeight: '500' }}>{item.label}</span>
              </Link>
            )}
          </div>
        ))}
      </nav>
    </aside>
  )
}